import z from 'zod';
import type { BankAccountFormatRuleRes } from '@dealstack/shared/lib/generated';

import { BANK_ACCOUNT_FORM_FIELDS } from './types';
import { ruleUsesClassicIbanForm } from './BankAccountForm.utils';
import {
	IbanFieldsetShape,
	refineIbanFieldset,
} from './childFieldsets/IbanFieldset/ibanFieldsetShape';

// Account metadata owned by the shell; per-country fields come from the child
// fieldset's shape fragment. Account holder and currency are captured for every
// bank account, whichever entry format is active.
const BankAccountShellShape = z.object({
	[BANK_ACCOUNT_FORM_FIELDS.country_code]: z.preprocess(
		(v) => v ?? '',
		z.string().min(1, 'Country is required')
	),
	[BANK_ACCOUNT_FORM_FIELDS.account_name]: z.string().min(1, 'Account holder name is required'),
	[BANK_ACCOUNT_FORM_FIELDS.currency]: z.preprocess(
		(v) => v ?? '',
		z.string().min(1, 'Currency is required')
	),
});

// null-tolerant: option selects clear to null, text inputs to ''
const buildRuleFieldSchema = (field: BankAccountFormatRuleRes['fields'][number]) =>
	z.preprocess(
		(value) => value ?? '',
		z
			.string()
			.min(field.required ? 1 : 0, `${field.label} is required`)
			.refine(
				(value) => value === '' || !field.pattern || new RegExp(field.pattern).test(value),
				{ message: `${field.label} has an invalid format` }
			)
	);

// The classic IBAN fieldset requires only the IBAN; a format rule replaces it with
// its own `fields` config, validated per the rule's required/pattern flags.
// An IBAN-carrying rule keeps the classic shape — it renders the classic form, and
// stripping format_rule_id/identifiers here is what keeps its submission classic too.
export const buildBankAccountShape = (rule: BankAccountFormatRuleRes | null) => {
	if (!rule || ruleUsesClassicIbanForm(rule)) {
		return BankAccountShellShape.extend(IbanFieldsetShape.shape).superRefine(
			refineIbanFieldset
		);
	}

	// Bank name and BIC are the lookup-derived values a rule tab has to carry: no field
	// renders either, but a successful check fills them in and they belong to the account
	// whatever format was used. A shape that omits a field drops it — the submitted
	// values are the PARSED ones — so leaving one out loses it on save.
	// The BIC collapses '' to undefined because the tab has no field to re-enter it in:
	// empty means "not looked up", and '' would delete the stored row rather than leave
	// it for the identifier sweep (see syncIdentifiers in bankAccount.repository).
	// iban and account_number stay omitted on purpose — the user's own typed account
	// number must not be overwritten, and a stale iban must stay sweepable.
	return BankAccountShellShape.extend({
		[BANK_ACCOUNT_FORM_FIELDS.bank_name]: z.preprocess((v) => v ?? '', z.string()),
		[BANK_ACCOUNT_FORM_FIELDS.bic]: z.preprocess((v) => v || undefined, z.string().optional()),
		[BANK_ACCOUNT_FORM_FIELDS.format_rule_id]: z.preprocess((v) => v ?? '', z.string().min(1)),
		[BANK_ACCOUNT_FORM_FIELDS.identifiers]: z.object(
			Object.fromEntries(rule.fields.map((field) => [field.key, buildRuleFieldSchema(field)]))
		),
	});
};
